import { useState } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Card, CardHeader, Button } from '../ui';
import type { MetrikaDailyTrendPoint, MetrikaPrefillData } from '../../lib/api';

type MetrikaTab = 'traffic' | 'goals' | 'conversion';

const TAB_LABELS: Record<MetrikaTab, string> = {
  traffic: 'Трафик',
  goals: 'Целевые действия',
  conversion: 'Конверсия',
};

interface MetrikaSectionProps {
  connected: boolean;
  onConnect: () => void;
  metrics: MetrikaPrefillData | null;
  trend: MetrikaDailyTrendPoint[];
  loading: boolean;
}

export function MetrikaSection({ connected, onConnect, metrics, trend, loading }: MetrikaSectionProps) {
  const [tab, setTab] = useState<MetrikaTab>('traffic');

  if (!connected) {
    return (
      <Card className="text-center py-10">
        <p className="text-gray-600 mb-4">Подключите Яндекс.Метрику, чтобы видеть трафик и целевые действия на Обзоре</p>
        <Button onClick={onConnect}>Подключить Яндекс.Метрику</Button>
      </Card>
    );
  }

  const visits = metrics?.websiteClicks ?? 0;
  const goalReaches = trend.reduce((sum, p) => sum + (p.goalReaches ?? 0), 0);
  const conversionRate = visits > 0 ? Math.round((goalReaches / visits) * 1000) / 10 : 0;

  return (
    <Card>
      <CardHeader
        title="Эффект на сайт (Яндекс.Метрика)"
        action={
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {(Object.keys(TAB_LABELS) as MetrikaTab[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                aria-pressed={tab === key}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  tab === key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {TAB_LABELS[key]}
              </button>
            ))}
          </div>
        }
      />

      {loading ? (
        <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Загрузка...</div>
      ) : trend.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Недостаточно данных за выбранный период</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
            <div>
              <p className="text-2xl font-bold text-gray-900">{visits}</p>
              <p className="text-sm text-gray-500">Визиты</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{goalReaches}</p>
              <p className="text-sm text-gray-500">Целевые действия</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{conversionRate}%</p>
              <p className="text-sm text-gray-500">Конверсия</p>
            </div>
          </div>
          <div />

          <div className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="visits" name="Визиты" stroke="#4f46e5" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="goalReaches" name="Целевые действия" stroke="#16a34a" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2">
            {metrics?.trackedGoalName ? (
              <div className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-gray-50">
                <span className="text-gray-700">{metrics.trackedGoalName}</span>
                <span className="font-semibold text-gray-900">{goalReaches}</span>
              </div>
            ) : (
              <p className="text-sm text-gray-400">Цель не настроена — выберите её на вкладке Аналитика</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
